import React, { FC, useMemo, useState } from 'react';
import '../scaling-dashboard.scss';
import './web-work-mix.scss';
import { useWebWorkMix } from './useWebWorkMix';
import type {
  WebWorkKind,
  WebWorkKindStats,
  WebWorkMixResponse,
  WebWorkSourceFilter,
  WebWorkWeeklyBucket,
} from './types';
import {
  DEFAULT_WEEKS,
  KIND_COLOURS,
  SOURCE_OPTIONS,
  WEB_WORK_KINDS,
  WEEK_OPTIONS,
} from './types';
import { KindStackChart, StackSeries } from './KindStackChart';
import { KindTable } from './KindTable';
import { HandoffReasonsPanel } from './HandoffReasonsPanel';
import { BlockedSection } from './BlockedSection';
import { CoveragePanel } from './CoveragePanel';
import {
  DASH,
  fmtDelta,
  fmtInt,
  fmtShare,
  fmtSpan,
  fmtWeekRange,
  fmtWeekdaySpan,
} from './format';

/**
 * Platform Stats → Web Work Mix. What kinds of web tasks humans do for us, on
 * which sites, and why the browser agent couldn't finish them — week over
 * week, current week to date. Read-only over persisted classifier verdicts.
 */

export function kindSeries(
  weekly: WebWorkWeeklyBucket[],
  kinds: WebWorkKindStats[],
): StackSeries[] {
  const ranked: WebWorkKind[] = kinds.map((k) => k.kind);
  const present = new Set(ranked);
  WEB_WORK_KINDS.forEach((k) => {
    if (!present.has(k) && weekly.some((w) => (w.byKind[k] ?? 0) > 0))
      ranked.push(k);
  });
  return ranked.map((k) => ({
    key: k,
    label: kinds.find((x) => x.kind === k)?.label ?? k,
    colour: KIND_COLOURS[k],
    values: weekly.map((w) => w.byKind[k] ?? 0),
  }));
}

export function topCombo(kinds: WebWorkKindStats[]): {
  label: string;
  count: number;
} | null {
  // Exact (kind, reason, site) tuples: each reason carries the sites of its
  // own rows, so a reason's domain count is the count of that triple.
  let best: { label: string; count: number; known: boolean } | null = null;
  for (const k of kinds) {
    for (const r of k.reasons) {
      for (const d of r.domains) {
        const known = d.domain !== 'unknown';
        const better =
          best === null ||
          (known && !best.known) ||
          (known === best.known && d.count > best.count);
        if (better) {
          best = {
            label: `${k.label} · ${d.domain} · ${r.label}`,
            count: d.count,
            known,
          };
        }
      }
    }
  }
  return best === null ? null : { label: best.label, count: best.count };
}

const Tiles: FC<{ data: WebWorkMixResponse }> = ({ data }) => {
  const current = data.weekly.find((w) => w.isCurrent) ?? null;
  const lastFull = [...data.weekly].reverse().find((w) => !w.isCurrent) ?? null;
  const delta = fmtDelta(data.currentWeek.deltaPct);
  const spanLabel = fmtWeekdaySpan(data.currentWeek.through);
  const combo = topCombo(data.kinds);
  return (
    <div className='wwm-tiles' data-testid='wwm-tiles'>
      <div className='wwm-tile' data-testid='tile-this-week'>
        <div className='k'>Human web work · this week so far</div>
        <div className='v'>{fmtInt(data.currentWeek.total)}</div>
        <div className='d'>
          {fmtInt(current?.webActions ?? 0)} web_actions ·{' '}
          {fmtInt(current?.interventions ?? 0)} interventions
        </div>
      </div>
      <div className='wwm-tile' data-testid='tile-same-span'>
        <div className='k'>Same span last week ({spanLabel})</div>
        <div className='v'>{fmtInt(data.currentWeek.priorWeekSameSpan)}</div>
        <div className='d'>
          <span className={`wwm-delta ${delta.direction}`}>{delta.label}</span>{' '}
          WoW
        </div>
      </div>
      <div className='wwm-tile' data-testid='tile-last-full'>
        <div className='k'>
          Last full week
          {lastFull ? ` (${fmtWeekRange(lastFull.weekStart)})` : ''}
        </div>
        <div className='v'>{fmtInt(lastFull?.total ?? 0)}</div>
        <div className='d'>
          {fmtInt(lastFull?.webActions ?? 0)} web_actions ·{' '}
          {fmtInt(lastFull?.interventions ?? 0)} interventions
        </div>
      </div>
      <div className='wwm-tile' data-testid='tile-agent-tried'>
        <div className='k'>Agent tried first</div>
        <div className='v'>
          {fmtShare(current?.agentAttempted ?? 0, current?.total ?? 0)}
        </div>
        <div className='d'>
          of this week&apos;s human work had a browser attempt
        </div>
      </div>
      <div className='wwm-tile' data-testid='tile-top-combo'>
        <div className='k'>Top kind × site × reason</div>
        <div className='v small'>{combo?.label ?? DASH}</div>
        <div className='d'>
          {combo
            ? `${fmtInt(combo.count)} this window`
            : 'no classified work yet'}
        </div>
      </div>
    </div>
  );
};

const WebWorkMixDashboard: FC = () => {
  const [weeks, setWeeks] = useState<number>(DEFAULT_WEEKS);
  const [source, setSource] = useState<WebWorkSourceFilter>('all');
  const { data, loading, warming, error, retry } = useWebWorkMix(weeks, source);

  const series = useMemo(
    () => (data ? kindSeries(data.weekly, data.kinds) : []),
    [data],
  );

  const isEmpty = data !== null && data.coverage.totalRows === 0;
  const spanLabel = data ? fmtWeekdaySpan(data.currentWeek.through) : DASH;

  return (
    <div className='scaling-dashboard web-work-mix' data-testid='web-work-mix'>
      <header className='scaling-header'>
        <div>
          <h2>Web Work Mix</h2>
          <p className='scaling-subtitle'>
            What kinds of web tasks humans are doing for us, on which sites, and
            why the browser agent couldn&apos;t finish them. Week over week,
            current week to date.
          </p>
        </div>
      </header>

      <div className='scaling-controls wwm-controls'>
        <span className='wwm-pill'>Weeks</span>
        <div className='day-selector' role='group' aria-label='Weeks'>
          {WEEK_OPTIONS.map((w) => (
            <button
              key={w}
              type='button'
              className={`day-btn ${weeks === w ? 'active' : ''}`}
              onClick={() => setWeeks(w)}
              aria-pressed={weeks === w}
            >
              {w}
            </button>
          ))}
        </div>
        <span className='wwm-pill'>Source</span>
        <div className='day-selector' role='group' aria-label='Source'>
          {SOURCE_OPTIONS.map((s) => (
            <button
              key={s.value}
              type='button'
              className={`day-btn ${source === s.value ? 'active' : ''}`}
              onClick={() => setSource(s.value)}
              aria-pressed={source === s.value}
            >
              {s.label}
            </button>
          ))}
        </div>
        {data && (
          <span className='wwm-pill warn' data-testid='current-week-pill'>
            Current week ={' '}
            {fmtSpan(data.currentWeek.start, data.currentWeek.through)} (so far)
          </span>
        )}
        <a className='wwm-link' href='#wwm-definitions'>
          Definitions
        </a>
      </div>

      {loading && (
        <div className='scaling-card wwm-status' data-testid='wwm-loading'>
          {warming ? (
            <>
              <span className='wwm-spinner' aria-hidden='true' />
              Warming the report cache — this can take up to a minute the first
              time. Retrying automatically…
            </>
          ) : (
            <>
              <span className='wwm-spinner' aria-hidden='true' />
              Loading…
            </>
          )}
        </div>
      )}

      {!loading && error && (
        <div className='scaling-card wwm-status error' data-testid='wwm-error'>
          {error === 'timeout'
            ? 'The report took too long to compute.'
            : 'Failed to load the Web Work Mix report.'}{' '}
          <button type='button' className='wwm-linklike' onClick={retry}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {data.coverage.pendingRows > 0 && (
            <div className='wwm-banner' data-testid='wwm-partial'>
              <b>Partial classification.</b> {fmtInt(data.coverage.pendingRows)}{' '}
              of {fmtInt(data.coverage.totalRows)} rows in this window are still
              waiting for the nightly classifier and are not in the charts yet.
            </div>
          )}

          {isEmpty ? (
            <div className='scaling-card wwm-status' data-testid='wwm-empty'>
              No human web work in the last {fmtInt(data.weeks)} weeks for this
              source. Try a wider range or another source.
            </div>
          ) : (
            <>
              <Tiles data={data} />

              <h3>All human web work, week over week — stacked by task kind</h3>
              <div className='scaling-card wwm-card'>
                <KindStackChart
                  buckets={data.weekly}
                  series={series}
                  testId='kind-chart'
                  ariaLabel='Human web work per ISO week, stacked by task kind'
                />
                <p className='wwm-note'>
                  Bar = web_action tasks created + interventions started in the
                  ISO week (Mon–Sun, UTC). Hatched bar = current week so far.
                  Segments ordered by overall frequency (largest at the bottom).
                  Click a legend item to hide/show it.
                </p>
              </div>

              <h3>
                Task kinds — most to least frequent{' '}
                <span className='wwm-pill'>
                  last {fmtInt(data.weeks - 1)} full weeks + this week
                </span>
              </h3>
              <div className='scaling-card wwm-card wwm-card--table'>
                <KindTable
                  kinds={data.kinds}
                  weekly={data.weekly}
                  spanLabel={spanLabel}
                />
              </div>

              <h3>Why it became human work — hand-off reasons</h3>
              <div className='scaling-card wwm-card wwm-card--table'>
                <HandoffReasonsPanel
                  reasons={data.handoffReasons}
                  kinds={data.kinds}
                />
              </div>

              <BlockedSection blocked={data.blocked} />
            </>
          )}

          <CoveragePanel
            coverage={data.coverage}
            definitions={data.definitions}
          />
          <p className='wwm-note wwm-generated'>
            Generated {new Date(data.generatedAt).toUTCString()} · read from
            persisted classifier verdicts; no LLM calls on this page.
          </p>
        </>
      )}
    </div>
  );
};

export default WebWorkMixDashboard;
